import { create } from "zustand";
import { CURRENCIES, type Currency } from "../types.ts";

interface UiState {
  /**
   * Sube cada vez que los colores de una organización quedan escritos en el
   * DOM.
   *
   * Existe para los gráficos. Recharts no entiende `var(--chart-1)` de forma
   * confiable, así que `useChartTheme` resuelve los tokens a colores concretos
   * y se los queda — y ese momento es siempre anterior a que el shell aplique
   * la paleta de la clínica, porque los efectos de los hijos corren antes que
   * los del padre. Sin este aviso, un gráfico se dibuja con la paleta del
   * sistema hasta que algo lo desmonte, y la clínica ve barras de un color que
   * nunca eligió.
   */
  palette: number;
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  /**
   * La sede que se está mirando, o null para todas.
   *
   * Vive acá y no en la dirección porque no es de una pantalla: se elige una
   * vez y acompaña. Se guarda en el navegador para que quien trabaja siempre
   * en la misma sede no tenga que elegirla cada mañana.
   */
  activeLocationId: string | null;
  /**
   * En qué moneda se está mirando y cargando la plata.
   *
   * La clínica cobra en pesos y en dólares, y no hay conversión en ningún
   * lado: cada pantalla de finanzas muestra los movimientos de la moneda
   * elegida y suma sólo esos. Vive acá, y no en la dirección de cada pantalla,
   * porque acompaña a quien trabaja —el libro, el reporte por tratamiento, el
   * retorno de campañas y el acumulado del CRM son la misma pregunta hecha en
   * la misma moneda— y se guarda en el navegador para no volver a elegirla
   * cada mañana.
   */
  currency: Currency;
  paletteApplied: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveLocation: (id: string | null) => void;
  setCurrency: (currency: Currency) => void;
}

const STORAGE_KEY = "dd-theme";
const LOCATION_KEY = "dd-location-id";
const CURRENCY_KEY = "dd-currency";
const SIDEBAR_COLLAPSED_KEY = "dd-sidebar-collapsed";

/** Lo guardado, si sigue siendo una moneda que la app conoce. */
function storedCurrency(): Currency {
  const value = globalThis.localStorage?.getItem(CURRENCY_KEY);
  return CURRENCIES.includes(value as Currency) ? (value as Currency) : "ARS";
}

export const useUiStore = create<UiState>((set) => ({
  palette: 0,
  sidebarOpen: false,
  sidebarCollapsed:
    globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_KEY) === "true",
  activeLocationId: globalThis.localStorage?.getItem(LOCATION_KEY) ?? null,
  currency: storedCurrency(),
  paletteApplied: () => set((state) => ({ palette: state.palette + 1 })),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setSidebarCollapsed: (sidebarCollapsed) => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    set({ sidebarCollapsed });
  },
  setActiveLocation: (activeLocationId) => {
    if (activeLocationId) localStorage.setItem(LOCATION_KEY, activeLocationId);
    else localStorage.removeItem(LOCATION_KEY);
    set({ activeLocationId });
  },
  setCurrency: (currency) => {
    localStorage.setItem(CURRENCY_KEY, currency);
    set({ currency });
  },
}));

/**
 * Deja la aplicación en claro, siempre, antes del primer pintado.
 *
 * El producto tiene un solo tema. Además de no escribir la clase `dark`, borra
 * la preferencia que hubiera quedado guardada: sin el botón para apagarlo, a
 * quien tenía el oscuro puesto no le quedaría forma de volver, y la clase se
 * habría vuelto a poner en cada carga.
 */
export function initTheme(): void {
  document.documentElement.classList.remove("dark");
  globalThis.localStorage?.removeItem(STORAGE_KEY);
}
